import { useEffect, useRef, useState, useCallback } from 'react';
// Cloudflare Turnstile script URL
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
/**
 * useTurnstile React hook
 * @param {string} widgetId - DOM id for the widget container (must be unique per page)
 * @param {object} options - { sitekey, theme, size, action, invisible }
 * @returns {object} { ready, error, renderWidget, getTurnstileToken, resetWidget, token }
 */
export function useTurnstile(widgetId = 'turnstile-widget', options = {}) {
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(null);
    const [token, setToken] = useState(null);
    const widgetRef = useRef(null);
    const widgetRendered = useRef(false);
    const widgetInstanceId = useRef(null);
    // Load Turnstile script
    useEffect(() => {
        if (window.turnstile) {
            setReady(true);
            return;
        }
        const script = document.createElement('script');
        script.src = TURNSTILE_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => setReady(true);
        script.onerror = () => setError('Failed to load Turnstile script.');
        document.body.appendChild(script);
        return () => {
            document.body.removeChild(script);
        };
    }, []);
    // Render the widget
    const renderWidget = useCallback((containerId = widgetId, opts = {}) => {
        if (!window.turnstile) {
            setError('Turnstile not loaded');
            return;
        }
        if (widgetRendered.current)
            return;
        const sitekey = opts.sitekey || options.sitekey || import.meta.env.VITE_TURNSTILE_SITE_KEY;
        if (!sitekey) {
            setError('Turnstile sitekey is missing');
            return;
        }
        const params = {
            sitekey,
            theme: opts.theme || options.theme || 'light',
            size: opts.size || options.size || 'invisible',
            action: opts.action || options.action || 'register',
            callback: (token) => setToken(token),
            'error-callback': () => setError('Turnstile error'),
            'expired-callback': () => setToken(null),
            ...opts,
        };
        widgetInstanceId.current = window.turnstile.render(`#${containerId}`, params);
        widgetRendered.current = true;
    }, [widgetId, options]);
    // Get token (executes invisible widget)
    const getTurnstileToken = useCallback(async (containerId = widgetId) => {
        if (!window.turnstile)
            throw new Error('Turnstile not loaded');
        if (!widgetRendered.current)
            throw new Error('Turnstile widget not rendered');
        setToken(null);
        return new Promise((resolve, reject) => {
            window.turnstile.execute(widgetInstanceId.current);
            const check = () => {
                if (token) {
                    resolve(token);
                }
                else if (error) {
                    reject(error);
                }
                else {
                    setTimeout(check, 50);
                }
            };
            check();
        });
    }, [widgetId, token, error]);
    // Reset widget
    const resetWidget = useCallback((containerId = widgetId) => {
        if (window.turnstile && widgetInstanceId.current) {
            window.turnstile.reset(widgetInstanceId.current);
            setToken(null);
        }
    }, [widgetId]);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (window.turnstile && widgetInstanceId.current) {
                window.turnstile.remove(widgetInstanceId.current);
            }
        };
    }, []);
    return {
        ready,
        error,
        renderWidget,
        getTurnstileToken,
        resetWidget,
        token,
    };
}
